'use client';

import Image from 'next/image';

import { ArrowRight } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { getAllTemplates } from '@/lib/template-data';
import { cn } from '@/lib/utils';

interface TemplatesListProps {
  systemFilter?: string;
  categoryFilter?: string;
  limit?: number;
  className?: string;
  variant?: 'grid' | 'slider';
}

export function TemplatesList({
  systemFilter,
  categoryFilter,
  limit,
  className,
  variant = 'grid',
}: TemplatesListProps) {
  const { t } = useTranslation();

  // Get templates with translations
  const allTemplates = getAllTemplates(t.templates);

  // Filter templates based on system and category
  let filteredTemplates = allTemplates.filter((template) => {
    const matchesSystem = systemFilter
      ? template.supportedSystems.some((system) => system.name === systemFilter)
      : true;
    const matchesCategory = categoryFilter
      ? template.category === categoryFilter
      : true;
    return matchesSystem && matchesCategory;
  });

  // Apply limit if provided
  if (limit) {
    filteredTemplates = filteredTemplates.slice(0, limit);
  }

  if (filteredTemplates.length === 0) {
    return null;
  }

  const TemplateCard = ({
    template,
    index,
  }: {
    template: (typeof filteredTemplates)[0];
    index: number;
  }) => (
    <Link
      key={index}
      href={`/templates/${template.slug}`}
      className="group block h-full"
    >
      <Card className="h-full transition-all duration-300 hover:border-primary/50 hover:shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            {/* Workflow Visualization - Simplified */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {template.workflow.slice(0, 3).map((step, stepIndex) => {
                const StepIcon = step.icon;
                return (
                  <div key={stepIndex} className="flex items-center gap-2">
                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-md border bg-secondary/50',
                        step.final &&
                          'border-primary bg-primary text-primary-foreground',
                      )}
                    >
                      <StepIcon className="h-4 w-4" />
                    </div>
                    {stepIndex < Math.min(template.workflow.length, 3) - 1 && (
                      <ArrowRight className="h-3 w-3 text-muted-foreground/50 rtl:rotate-180" />
                    )}
                  </div>
                );
              })}
            </div>

            <CardTitle className="text-xl font-medium leading-tight transition-colors group-hover:text-primary">
              {template.name
                .replace(/ with AI/g, '')
                // Arabic cards carry the same redundant tail; anchor to end of
                // string so mid-string uses survive.
                .replace(/\s*بالذكاء الاصطناعي\s*$/, '')}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="line-clamp-3 text-sm text-muted-foreground">
            {template.description}
          </p>

          {/* Supported Systems - Mini */}
          {template.supportedSystems &&
            template.supportedSystems.length > 0 && (
              <div className="mt-4 flex items-center gap-2 border-t pt-4">
                <span className="text-xs text-muted-foreground">
                  {t.common['Works with']}:
                </span>
                <div className="flex -space-x-2 transition-all hover:space-x-1">
                  {template.supportedSystems.slice(0, 4).map((system) => (
                    <div
                      key={system.name}
                      className="relative h-6 w-6 overflow-hidden rounded-full border border-background bg-white shadow-sm"
                      title={system.name}
                    >
                      <Image
                        src={system.src}
                        alt={system.name}
                        fill
                        className="object-cover p-1"
                      />
                    </div>
                  ))}
                  {template.supportedSystems.length > 4 && (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full border border-background bg-muted text-[10px] font-medium">
                      +{template.supportedSystems.length - 4}
                    </div>
                  )}
                </div>
              </div>
            )}
        </CardContent>
      </Card>
    </Link>
  );

  if (variant === 'slider') {
    return (
      <div className={cn('flex flex-col gap-8', className)}>
        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {filteredTemplates.map((template, index) => (
              <CarouselItem
                key={index}
                className="pl-4 md:basis-1/2 lg:basis-1/3"
              >
                <TemplateCard template={template} index={index} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-4 md:-left-12" />
          <CarouselNext className="-right-4 md:-right-12" />
        </Carousel>

        <div className="flex justify-center">
          <Link
            href={
              systemFilter ? `/templates?system=${systemFilter}` : '/templates'
            }
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {systemFilter
              ? `${t.common['View all']} ${systemFilter} ${t.common.templates}`
              : t.common['View all templates']}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-8', className)}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {filteredTemplates.map((template, index) => (
          <TemplateCard key={index} template={template} index={index} />
        ))}
      </div>

      <div className="flex justify-center">
        <Link
          href={
            systemFilter ? `/templates?system=${systemFilter}` : '/templates'
          }
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {systemFilter
            ? `${t.common['View all']} ${systemFilter} ${t.common.templates}`
            : t.common['View all templates']}
          <ArrowRight className="h-4 w-4 rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
}
